import { KPICard } from "@/components/accounting/kpi-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWorkspacePage } from "@/components/workspace/use-workspace-page";
import {
	useListExceptions,
	useListPlannedActions,
} from "@/hooks/use-manufacturing";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/manufacturing/")({
	component: ManufacturingDashboard,
});

function ManufacturingDashboard() {
	const { t } = useTranslation("manufacturing");
	const navigate = useNavigate();

	const { data: exceptions } = useListExceptions({ status: "open" });
	const { data: planned } = useListPlannedActions({ status: "planned" });
	const { data: approved } = useListPlannedActions({ status: "approved" });

	const openExceptions = exceptions?.exceptions?.length ?? 0;
	const criticalExceptions =
		exceptions?.exceptions?.filter((e) => e.severity === "critical")?.length ??
		0;
	const plannedCount = planned?.actions?.length ?? 0;
	const approvedCount = approved?.actions?.length ?? 0;
	const [searchText, setSearchText] = useState("");

	const statusBar = useMemo(
		() => [
			{
				id: "summary",
				content: `${openExceptions} ${t("status_bar_open")} • ${criticalExceptions} ${t("status_bar_critical")}`,
				align: "left" as const,
			},
		],
		[openExceptions, criticalExceptions, t],
	);

	useWorkspacePage({ statusBar });

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => navigate({ to: "/manufacturing/exceptions" })}
				>
					{t("exceptions")}
				</Button>
				<Button
					size="sm"
					className="h-9"
					onClick={() => navigate({ to: "/manufacturing/board" })}
				>
					{t("board")}
				</Button>
			</div>
			<div className="flex flex-col gap-3 p-3 overflow-y-auto flex-1">
			{/* KPI Row */}
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
				<KPICard
					label={t("kpi_exceptions")}
					amount={String(openExceptions)}
					onClick={() => navigate({ to: "/manufacturing/exceptions" })}
				/>
				<KPICard
					label={t("kpi_stockout_risk")}
					amount={String(criticalExceptions)}
					onClick={() =>
						navigate({
							to: "/manufacturing/exceptions",
						})
					}
				/>
				<KPICard
					label={t("kpi_actions_planned")}
					amount={String(plannedCount)}
					onClick={() => navigate({ to: "/manufacturing/board" })}
				/>
				<KPICard
					label={t("kpi_actions_approved")}
					amount={String(approvedCount)}
					onClick={() => navigate({ to: "/manufacturing/actions" })}
				/>
				<KPICard
					label={t("kpi_production_orders")}
					amount="—"
					onClick={() => navigate({ to: "/manufacturing/production" })}
				/>
			</div>

			{/* Exception summary */}
			{openExceptions > 0 && (
				<div className="rounded-md border bg-background p-3">
					<h3 className="mb-2 text-xs font-semibold text-muted-foreground uppercase">
						{t("exceptions")}
					</h3>
					<div className="divide-y">
						{exceptions?.exceptions?.slice(0, 5).map((exc) => (
							<div
								key={exc.id}
								className="flex items-center gap-2 py-1.5 text-xs"
							>
								<span
									className={`h-2 w-2 shrink-0 rounded-full ${
										exc.severity === "critical"
											? "bg-red-500"
											: exc.severity === "high"
												? "bg-amber-500"
												: "bg-blue-400"
									}`}
								/>
								<span className="truncate flex-1">{exc.message}</span>
								<span className="shrink-0 text-muted-foreground">
									{exc.productId?.slice(0, 8)}
								</span>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Recent planned actions */}
			{plannedCount > 0 && (
				<div className="rounded-md border bg-background p-3">
					<h3 className="mb-2 text-xs font-semibold text-muted-foreground uppercase">
						{t("planned_actions")}
					</h3>
					<div className="divide-y">
						{planned?.actions?.slice(0, 8).map((action) => (
							<div
								key={action.id}
								className="flex items-center gap-3 py-1.5 text-xs"
							>
								<span
									className={`h-2 w-2 shrink-0 rounded-full ${
										action.urgency === "critical"
											? "bg-red-500"
											: action.urgency === "high"
												? "bg-amber-500"
												: "bg-blue-400"
									}`}
								/>
								<span className="w-16 shrink-0 font-medium">
									{t(`type_${action.actionType}`)}
								</span>
								<span className="truncate flex-1">
									{action.productId?.slice(0, 8)}
								</span>
								<span className="shrink-0 tabular-nums">
									{Number(action.qty).toLocaleString("ru-RU")}
								</span>
								<span className="shrink-0 text-muted-foreground">
									{action.targetDate}
								</span>
							</div>
						))}
					</div>
				</div>
			)}
			</div>
		</div>
	);
}
